import type { PayScheduleResponse } from '../../pay-config/types';

export interface PayPeriod {
  start: string;
  end: string;
}

const MS_PER_DAY = 86_400_000;

function toUtc(iso: string): number {
  const [y, m, d] = iso.split('-').map(Number);
  return Date.UTC(y!, m! - 1, d!);
}

function fromUtc(ms: number): string {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}

const addDays = (iso: string, days: number) => fromUtc(toUtc(iso) + days * MS_PER_DAY);
const lastDayOfMonth = (year: number, month1: number) => new Date(Date.UTC(year, month1, 0)).getUTCDate();
const pad = (n: number) => String(n).padStart(2, '0');

/**
 * The pay period of a company pay schedule that contains `onDate`, derived from the schedule's own data (its frequency and the start date it counts from).
 * Weekly and biweekly count from the schedule's `payPeriodStartDate`; semimonthly uses the 1st to the 15th and the 16th to month end; monthly the calendar month;
 * quarterly the calendar quarter. Returns null when the schedule cannot say (no start date for a weekly or biweekly schedule, or an unknown frequency), in
 * which case the caller leaves the dates for the person to enter. A SUGGESTION only: the dates stay editable and the server accepts whatever the person confirms.
 * The semimonthly and quarterly boundaries are an assumption pending payroll confirmation (BUSINESS RULE CONFIRMATION REQUIRED).
 */
export function payPeriodContaining(schedule: Pick<PayScheduleResponse, 'frequency' | 'payPeriodStartDate'>, onDate: string): PayPeriod | null {
  const [year, month] = onDate.split('-').map(Number) as [number, number, number];
  const day = Number(onDate.slice(8, 10));
  switch (schedule.frequency) {
    case 1:
    case 2: {
      if (!schedule.payPeriodStartDate) return null;
      const length = schedule.frequency === 1 ? 7 : 14;
      const diffDays = Math.round((toUtc(onDate) - toUtc(schedule.payPeriodStartDate)) / MS_PER_DAY);
      const start = addDays(schedule.payPeriodStartDate, Math.floor(diffDays / length) * length);
      return { start, end: addDays(start, length - 1) };
    }
    case 3:
      return day <= 15
        ? { start: `${year}-${pad(month)}-01`, end: `${year}-${pad(month)}-15` }
        : { start: `${year}-${pad(month)}-16`, end: `${year}-${pad(month)}-${pad(lastDayOfMonth(year, month))}` };
    case 4:
      return { start: `${year}-${pad(month)}-01`, end: `${year}-${pad(month)}-${pad(lastDayOfMonth(year, month))}` };
    case 5: {
      const firstMonth = Math.floor((month - 1) / 3) * 3 + 1;
      const lastMonth = firstMonth + 2;
      return { start: `${year}-${pad(firstMonth)}-01`, end: `${year}-${pad(lastMonth)}-${pad(lastDayOfMonth(year, lastMonth))}` };
    }
    default:
      return null;
  }
}

/** The suggested pay day for a period: the period's last day, or today if that has passed (so a suggestion is never in the past by itself). */
export function suggestedCheckDate(period: PayPeriod, today: string): string {
  return period.end >= today ? period.end : today;
}

export function isoToday(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function daysBefore(iso: string, days: number): string {
  return addDays(iso, -days);
}
